// ./src/pages/private/FormsPage/FormsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  SortConfig,
} from "@/components/generic";
import {
  FormsTable,
  FormsEditModal,
  FormFieldsEditModal,
  FormCodeDrawer,
} from "@/components/features";
import { useForms } from "@/hooks";
import { Form, FormFilter } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { ENTITIES } from "@/config/userRoles";

export const FormsPage: React.FC<PageProps> = (props: PageProps) => {
  const { hasRightTo, isAuthenticated } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ formId?: string }>();

  // Parse URL to determine which modal to show
  const formIdFromUrl = params.formId;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode = !!formIdFromUrl && location.pathname.endsWith("/edit");
  const isEditFieldsMode =
    !!formIdFromUrl && location.pathname.endsWith("/edit-fields");
  const isCodeMode = !!formIdFromUrl && location.pathname.endsWith("/code");

  // Use the forms hook for all data management
  const {
    // Data state
    forms,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingFormId,

    // Data operations
    list,
    loadMore,
    refresh,
    get,

    // CRUD operations
    create,
    update,
    remove,

    // Error handling
    lastError,
    clearError,
  } = useForms({
    autoLoad: true,
    isAuthenticated,
    pageSize: 20,
  });

  // Local UI state
  const [formToDelete, setFormToDelete] = useState<Form | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [editedFormId, setEditedFormId] = useState<string | null>(null);

  const canCreate = hasRightTo(ENTITIES.FORMS, OPERATION_RIGHTS.CREATE);

  // Load form when in edit mode
  useEffect(() => {
    if ((isEditMode || isEditFieldsMode || isCodeMode) && formIdFromUrl) {
      // Fetch the form by ID
      const loadForm = async () => {
        await get(formIdFromUrl);
      };
      loadForm();
    }
  }, [isEditMode, isEditFieldsMode, isCodeMode, formIdFromUrl, get]);

  // Update editing form when we get data
  useEffect(() => {
    if ((isEditMode || isEditFieldsMode || isCodeMode) && formIdFromUrl) {
      const form = forms.find((f) => f.id === formIdFromUrl);
      if (form) {
        setEditingForm(form);
      }
    } else {
      setEditingForm(null);
    }
  }, [isEditMode, isEditFieldsMode, isCodeMode, formIdFromUrl, forms]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const combinedFilters: Partial<FormFilter> = {
        ...filters,
      };

      if (value.trim()) {
        combinedFilters.searchKeyword = value.trim();
      }

      // Update filters and reload data
      list(
        combinedFilters,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    },
    [sortBy, filters, list]
  );

  const handleSortBy = useCallback(
    (value: SortConfig[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, value, true);
    },
    [filters, list]
  );

  // Handle add form button click
  const handleAddForm = useCallback(() => {
    clearError();
    navigate("/forms/add");
  }, [navigate, clearError]);

  // Handle edit form
  const handleEditForm = useCallback(
    (form: Form) => {
      clearError();
      if (form.id) {
        navigate(`/forms/${form.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle edit form fields
  const handleEditFormFields = useCallback(
    (form: Form) => {
      clearError();
      if (form.id) {
        navigate(`/forms/${form.id}/edit-fields`);
      }
    },
    [navigate, clearError]
  );

  // Handle duplicate form
  const handleDuplicateForm = useCallback(
    (form: Form) => {
      clearError();
      setEditingForm({
        ...form,
        id: undefined,
        duplicateOfId: form.id,
        path: "",
        name: `${form.name} (Copy)`,
      });
      navigate("/forms/add");
    },
    [navigate, clearError]
  );

  // Handle get code click
  const handleGetCode = useCallback(
    (form: Form) => {
      if (form.id) {
        navigate(`/forms/${form.id}/code`);
      }
    },
    [navigate]
  );

  // Handle close code drawer
  const handleCloseCodeDrawer = useCallback(() => {
    navigate("/forms");
  }, [navigate]);

  // Handle remove form button click
  const handleRemoveFormClick = useCallback((form: Form) => {
    setFormToDelete(form);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (formToDelete?.id) {
      const success = await remove(formToDelete.id);
      if (success) {
        setFormToDelete(null);
        // If we're currently editing this form, close the sidebar
        if (formIdFromUrl === formToDelete.id) {
          navigate("/forms");
        }
      }
    }
  }, [formToDelete, remove, formIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setFormToDelete(null);
  }, []);

  // Handle form edit modal close
  const handleFormEditModalClose = useCallback(() => {
    setEditingForm(null);
    clearError();
    navigate("/forms");
  }, [navigate, clearError]);

  // Handle form fields edit modal close
  const handleFormFieldsEditModalClose = useCallback(() => {
    setEditingForm(null);
    clearError();
    navigate("/forms");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle form creation
  const handleCreateForm = useCallback(
    async (formData: any) => {
      const result = await create(formData);
      if (result) {
        setEditedFormId(result.id || null);
        // Navigate to field editing for the newly created form
        if (result.id) {
          navigate(`/forms/${result.id}/edit-fields`);
        }
      }
      return result;
    },
    [create, navigate]
  );

  // Handle form update
  const handleUpdateForm = useCallback(
    async (formId: string, formData: any) => {
      const result = await update(formId, formData);
      if (result) {
        setEditedFormId(result.id || null);
        // Stay on the edit page
        // User can manually close or navigate away
      }
      return result;
    },
    [update]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-form"
                  iconName="Plus"
                  onClick={handleAddForm}
                  disabled={
                    isCreating || !!removingFormId || loading || !canCreate
                  }
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingFormId || loading}
                />,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search forms..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Forms table */}
        <FormsTable
          forms={forms}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={handleSortBy}
          onLoadMore={loadMore}
          onEditForm={handleEditForm}
          onEditFormFields={handleEditFormFields}
          onDuplicateForm={handleDuplicateForm}
          onRemoveForm={handleRemoveFormClick}
          onGetCode={handleGetCode}
          removingFormId={removingFormId}
          editedItemId={editedFormId}
        />

        {/* Empty state when no forms */}
        <EmptyState
          display={isEmpty && !loading}
          title="No forms yet"
          description="Start by creating your first form to collect submissions from your website"
          actionIcon="Plus"
          actionLabel="Add Form"
          onAction={handleAddForm}
        />
      </Stack>

      {/* Form edit modal for create/edit */}
      <FormsEditModal
        opened={isAddMode || isEditMode}
        onClose={handleFormEditModalClose}
        form={
          isEditMode
            ? editingForm
            : isAddMode && editingForm
            ? editingForm
            : null
        }
        onCreateForm={handleCreateForm}
        onUpdateForm={handleUpdateForm}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Form fields edit modal */}
      <FormFieldsEditModal
        opened={isEditFieldsMode}
        onClose={handleFormFieldsEditModalClose}
        form={editingForm}
        formId={formIdFromUrl}
      />

      {/* Form code drawer */}
      <FormCodeDrawer
        opened={isCodeMode}
        onClose={handleCloseCodeDrawer}
        form={editingForm}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!formToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Form"
        message={`Are you sure you want to delete "${formToDelete?.name}"?`}
        details="This action cannot be undone. The form and all its fields will be permanently removed."
        confirmInProgress={removingFormId === formToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
